import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../../utils/cn";
import { STYLE_VALUES } from "../agentPayload";

// Átomos visuales compartidos por las secciones del editor de agentes.
//
// Son los MISMOS patrones que usa AiSettings (chips con roving tabindex,
// switches, títulos de sección, medidas de control) copiados acá a propósito:
// el editor es autocontenido y no importa nada de esa vista, que se reescribe
// en paralelo. Función normal a nivel de módulo, NUNCA componentes definidos
// dentro del render (remontarían el subtree y matarían el foco).

// GOTCHA _modern-forms.scss: sus reglas de input/select/textarea le ganan a
// Tailwind, por eso las utilidades de tamaño/tipografía van con variante !.
// !py-0: el padding vertical propio de _modern-forms descentraba el texto
// dentro de los 32px de md:!h-8. !text-base bajo md: Safari iOS hace zoom
// automático al enfocar un control con font-size < 16px y no vuelve solo.
export const CONTROL_CLS = cn(
  "w-full h-11 md:!h-8 rounded-md border border-border bg-bg !px-3 !py-0 !text-base md:!text-sm text-fg",
  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
  "disabled:opacity-50 disabled:cursor-not-allowed",
);

export const TEXTAREA_CLS = cn(
  "w-full rounded-md border border-border bg-bg !p-3 !text-base md:!text-sm text-fg resize-y",
  "placeholder:text-fg-subtle",
  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
  "disabled:opacity-50 disabled:cursor-not-allowed",
);

export const SECONDARY_BTN_CLS = cn(
  "inline-flex items-center justify-center gap-1.5 h-11 md:h-8 px-3 rounded-md text-xs font-medium",
  "border border-border bg-transparent text-fg hover:bg-bg-subtle",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors",
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
);

export const PRIMARY_BTN_CLS = cn(
  "inline-flex items-center justify-center gap-1.5 h-11 md:h-8 px-4 rounded-md text-xs font-medium",
  "hd-brand-solid bg-brand-500 text-white hover:bg-brand-600",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 transition-colors",
  "disabled:opacity-60 disabled:cursor-not-allowed",
);

// Única escala de título permitida (12px uppercase).
export function SectionTitle({ children }) {
  return (
    <h2 className="text-xs uppercase tracking-wide font-semibold text-fg-muted">{children}</h2>
  );
}

// Chip de un radiogroup. Roving tabindex: solo el chip activo entra en el orden
// de tabulación; entre chips se navega con flechas (patrón WAI-ARIA).
export function ModeChip({ active, onClick, label, describedBy }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-describedby={describedBy}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center h-11 md:h-8 px-3 rounded-md text-xs font-medium",
        "transition-colors duration-150 ease-out motion-reduce:transition-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "hd-brand-solid bg-brand-500 text-white" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
      )}
    >
      {label}
    </button>
  );
}

/**
 * Radiogroup de chips con roving tabindex. Las flechas mueven la selección y el
 * foco SIGUE a la selección; el foco se reencuentra tras el render que actualiza
 * aria-checked.
 */
export function ChipRadioGroup({ value, options, onChange, ariaLabel, describedBy }) {
  const groupRef = useRef(null);

  const handleKeyDown = (e) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    e.preventDefault();
    const idx = options.findIndex((o) => o.value === value);
    if (idx < 0) return;
    const delta = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
    const next = options[(idx + delta + options.length) % options.length];
    onChange(next.value);
    requestAnimationFrame(() => {
      groupRef.current?.querySelector('[aria-checked="true"]')?.focus();
    });
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className="inline-flex items-center gap-1 rounded-lg border border-border bg-bg p-1"
    >
      {options.map((o) => (
        <ModeChip
          key={o.value}
          active={value === o.value}
          onClick={() => onChange(o.value)}
          label={o.label}
          describedBy={describedBy}
        />
      ))}
    </div>
  );
}

// Badge chico de una línea (tier de modelo, "pide confirmación en el chat"…).
export function MetaBadge({ children, tone = "muted" }) {
  return (
    <span
      className={cn(
        "shrink-0 inline-flex items-center rounded border px-1.5 py-0.5 text-[12px] md:text-[10px] font-medium",
        tone === "brand"
          ? "border-brand-500 bg-brand-50 text-brand-700"
          : "border-border bg-bg-muted text-fg-muted",
      )}
    >
      {children}
    </span>
  );
}

// Contador de caracteres, en rojo al llegar al tope.
//
// `aria-live` queda en "off" mientras hay margen: anunciar cada tecla convierte
// el contador en ruido y tapa lo que el usuario sí necesita oír. Al 90% del tope
// pasa a "polite" — ahí el número deja de ser informativo y empieza a avisar.
export function CharCounter({ value, max }) {
  const nearLimit = max > 0 && value >= max * 0.9;
  return (
    <span
      className={cn(
        "text-[12px] md:text-[11px] tabular-nums shrink-0",
        value >= max ? "text-status-error" : "text-fg-muted",
      )}
      aria-live={nearLimit ? "polite" : "off"}
    >
      {value}/{max}
    </span>
  );
}

// Días de la semana, domingo=0 (convención del backend). Se PINTAN lunes
// primero (orden chileno) pero el value es el índice real.
export const DAY_PILLS = [
  { value: 1, label: "L", name: "lunes" },
  { value: 2, label: "M", name: "martes" },
  { value: 3, label: "X", name: "miércoles" },
  { value: 4, label: "J", name: "jueves" },
  { value: 5, label: "V", name: "viernes" },
  { value: 6, label: "S", name: "sábado" },
  { value: 0, label: "D", name: "domingo" },
];

// Botonera de días (aria-pressed). El estado vive en quien la usa.
export function DayPills({ value, onToggle, disabled }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t("Días de atención")} className="flex flex-wrap gap-1.5">
      {DAY_PILLS.map((d) => {
        const active = value.includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            aria-pressed={active}
            aria-label={t(d.name)}
            onClick={() => onToggle(d.value)}
            disabled={disabled}
            className={cn(
              "inline-flex items-center justify-center w-11 h-11 md:w-8 md:h-8 rounded-md text-xs font-medium border transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              active
                ? "hd-brand-solid bg-brand-500 border-transparent text-white"
                : "border-border text-fg-muted hover:text-fg hover:bg-bg-muted",
            )}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

// Perillas de personalización (capa 1 de estilo): mismas etiquetas que el
// Asistente IA, con las MISMAS claves de traducción.
//
// Los valores válidos viven en agentPayload.js (STYLE_VALUES) porque son
// contrato (el estilo inválido se normaliza al default); acá van solo las
// etiquetas visibles. Una opción sin etiqueta cae al propio valor en vez de
// quedar en blanco.
const STYLE_VALUE_LABELS = {
  profesional: "Profesional",
  cercano: "Cercano",
  formal: "Formal",
  menos: "Menos",
  normal: "Normal",
  mas: "Más",
  no: "No usar",
  pocos: "Pocos",
  si: "Sí",
  breve: "Breve",
  detallado: "Detallado",
  tu: "De tú",
  usted: "De usted",
};

const styleOptions = (key) =>
  STYLE_VALUES[key].map((value) => [value, STYLE_VALUE_LABELS[value] || value]);

export const STYLE_FIELDS = [
  {
    key: "style_tone",
    label: "Tono",
    desc: "Cómo suena el asistente al escribir.",
    options: styleOptions("style_tone"),
  },
  {
    key: "style_warmth",
    label: "Calidez",
    desc: "Cuánta cortesía y empatía agrega.",
    options: styleOptions("style_warmth"),
  },
  {
    key: "style_emoji",
    label: "Emojis",
    desc: "Si usa emojis en sus respuestas.",
    options: styleOptions("style_emoji"),
  },
  {
    key: "style_length",
    label: "Extensión",
    desc: "Qué tan largas son las respuestas.",
    options: styleOptions("style_length"),
  },
  {
    key: "style_address",
    label: "Tratamiento",
    desc: "Cómo se dirige al cliente.",
    options: styleOptions("style_address"),
  },
];
